import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Feature } from '@turf/turf';
import { Layer, geoJSON, marker, Icon } from 'leaflet';

import { Paciente } from './paciente.model';
import { ErrorHandlerService } from 'src/app/core/framework/error-handler.service';
import { DefaultService } from 'src/app/core/framework/default-service';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { DateTimeUtil } from 'src/app/core/framework/utils/date-time-util';
import { ListaResultset } from 'src/app/core/list/lista-resultset.interface';
import { ListaComponent } from 'src/app/core/list/lista.component';
import { DashboardAnaliseQuantidadeDeEndereco } from './dto/dashboard-analise-quantidade-endereco.dto';
import { PacienteFilter } from './paciente-filter';
import { AvaliacaoSumarioPacienteDTO } from './components/sumario-paciente/components/sumario-paciente-quadro-historico-atendimento/components/sumario-paciente-historico-avaliacao-item/dto/avaliacao-sumario-paciente-dto.interface';
import { QuestaoComRespostaDTO } from './dto/questao-com-resposta.dto';
import { AvaliacaoSumarioPacienteFilter } from './components/avaliacoes-por-paciente-lista/model/avaliacao-sumario-paciente.filter';
import { DashboardPacientesAgrupadosPorSexo } from 'src/app/dashboards/dashboard/dashboards/dashboard-carteira-de-cliente/model/dashboard-pacientes-agrupados-por-sexo.interface';
import { DashboardPacientesAgrupadosPorFaixaEtaria } from 'src/app/dashboards/dashboard/dashboards/dashboard-carteira-de-cliente/model/dashboard-pacientes-agrupados-por-faixa-etaria.interface';
import { DashboardPacientesAtivosEAssistidosAcumuladosPorMesAno } from 'src/app/dashboards/dashboard/dashboards/dashboard-carteira-de-cliente/model/dashboard-pacientes-ativos-assistidos-acumulados-por-mes-ano.interface';

@Injectable({
  providedIn: 'root'
})
export class PacienteService extends DefaultService<Paciente> {

  private dateTimeUtil = new DateTimeUtil();
  static URL = '/api/paciente/v1/pacientes';

  constructor(
    public httpClient: HttpClient,
    public errorHandlerService: ErrorHandlerService
  ) {
    super(
      httpClient,
      errorHandlerService,
      PacienteService.URL
    );
  }

  fetch(id: number): Observable<Paciente> {
    return this.httpClient.get<Paciente>(`${PacienteService.URL}/${id}`)
                          .pipe(
                            map((paciente: Paciente) => {
                              if ( paciente.dataNascimento ) {
                                paciente.dataNascimento = this.dateTimeUtil.stringIsoToDate('' + paciente.dataNascimento);
                              }

                              return paciente;
                            })
                            , catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  inserir(item: Paciente): Observable<Paciente> {
    return this.httpClient.post<Paciente>(PacienteService.URL, item, this.httpOptions)
                          .pipe(
                            map((paciente: Paciente) => {
                              if ( paciente.dataNascimento ) {
                                paciente.dataNascimento = this.dateTimeUtil.stringIsoToDate('' + paciente.dataNascimento);
                              }

                              return paciente;
                            })
                            , catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  ativar(id: number): Observable<Paciente> {
    return this.httpClient.put<Paciente>(`${PacienteService.URL}/ativar/${id}`, this.httpOptions)
                          .pipe(
                            map((paciente: Paciente) => {
                              if ( paciente.dataNascimento ) {
                                paciente.dataNascimento = this.dateTimeUtil.stringIsoToDate('' + paciente.dataNascimento);
                              }

                              return paciente;
                            })
                            , catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  atualizar(id: number, item: Paciente): Observable<Paciente> {
    return this.httpClient.put<Paciente>(`${PacienteService.URL}/${id}`, item, this.httpOptions)
                          .pipe(
                            map((paciente: Paciente) => {
                              if ( paciente.dataNascimento ) {
                                paciente.dataNascimento = this.dateTimeUtil.stringIsoToDate('' + paciente.dataNascimento);
                              }

                              return paciente;
                            })
                            , catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  buscarGeoJsonMultiPoint(): Observable<Feature> {
    return this.httpClient.get<Feature>(`${PacienteService.URL}/buscarGeoJsonMultiPoint`)
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  getGeojsonLayer(simbolo: Icon): Observable<Layer> {
    return this.buscarGeoJsonMultiPoint()
              .pipe(
                map((geojsonFeature: Feature) => {
                  return geoJSON(geojsonFeature, {
                    pointToLayer: (feature, latlng) => marker(latlng).setIcon(simbolo)
                  });
                })
              );
  }


  listarSomentePacientesInternos(filter: any, listComponent?: ListaComponent): Observable<ListaResultset<Paciente>> {
    const params = DefaultService.converterFiltroParaHttpParam(filter);

    return this.httpClient
               .get<ListaResultset<Paciente>>(`${PacienteService.URL}/listarSomentePacientesInternos`, {params})
               .pipe(
                catchError (err => {
                  if (listComponent) {
                      listComponent.setLoading(false);
                      listComponent.hasError = true;
                  }
                  return throwError(this.errorHandlerService.handle(err));
                })
               );
  }

  quantidadeDePaciente(status: string): Observable<number> {
    return this.httpClient.get<number>(`${PacienteService.URL}/quantidadeDePaciente?status=${status}`)
                .pipe(
                  catchError( err => throwError(this.errorHandlerService.handle(err)) )
                );
  }

  quantidadeDePacientesAtivosEAssistidos(): Observable<number> {
    return this.httpClient.get<number>(`${PacienteService.URL}/quantidadeDePacientesAtivosEAssistidos`)
                .pipe(
                  catchError( err => throwError(this.errorHandlerService.handle(err)) )
                );
  }

  dashboardAnaliseDeEndereco(status: string): Observable<DashboardAnaliseQuantidadeDeEndereco[]> {
    return this.httpClient.get<DashboardAnaliseQuantidadeDeEndereco[]>(`${PacienteService.URL}/dashboardAnaliseDeEndereco?status=${status}`)
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  pacientesAtivosEAssistidosAcumuladosPorMesAnoDosUltimos12Meses(): Observable<DashboardPacientesAtivosEAssistidosAcumuladosPorMesAno[]> {
    return this.httpClient.get<DashboardPacientesAtivosEAssistidosAcumuladosPorMesAno[]>(`${PacienteService.URL}/pacientesAtivosEAssistidosAcumuladosPorMesAnoDosUltimos12Meses`)
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  listarPacientesParaAnaliseDeEndereco(filter: PacienteFilter): Observable<ListaResultset<Paciente>> {
    const params = DefaultService.converterFiltroParaHttpParam(filter);
    return this.httpClient.get<ListaResultset<Paciente>>(`${PacienteService.URL}/listarPacientesParaAnaliseDeEndereco`, {params})
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  listarAvaliacoesPorPacientePaginado(filter: AvaliacaoSumarioPacienteFilter): Observable<ListaResultset<AvaliacaoSumarioPacienteDTO>> {
    const params = DefaultService.converterFiltroParaHttpParam(filter);
    return this.httpClient.get<ListaResultset<AvaliacaoSumarioPacienteDTO>>(`${PacienteService.URL}/listarAvaliacoesPorPacientePaginado`, {params})
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  listarQuestoesEmDestaquePorPaciente(idPaciente: number): Observable<QuestaoComRespostaDTO[]> {
    return this.httpClient.get<QuestaoComRespostaDTO[]>(`${PacienteService.URL}/listarQuestoesEmDestaquePorPaciente/${idPaciente}`, {})
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  dashboardPacientesAgrupadosPorSexo(status: string): Observable<DashboardPacientesAgrupadosPorSexo[]> {
    return this.httpClient.get<DashboardPacientesAgrupadosPorSexo[]>(`${PacienteService.URL}/dashboardPacientesAgrupadosPorSexo?status=${status}`)
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }

  dashboardPacientesAgrupadosPorFaixaEtaria(status: string): Observable<DashboardPacientesAgrupadosPorFaixaEtaria[]> {
    return this.httpClient.get<DashboardPacientesAgrupadosPorFaixaEtaria[]>(`${PacienteService.URL}/dashboardPacientesAgrupadosPorFaixaEtaria?status=${status}`)
                          .pipe(
                            catchError( err => throwError(this.errorHandlerService.handle(err)) )
                          );
  }
}
